import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv, faFileExcel, faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { secToHHMMSS, roundNumber } from "../util/formatter";
import { loadEntries } from "../../api/reportApi";
import DateRange from "../../components/DateRange";

const ReportPage = () => {
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [reportData, setReportData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleApplyFilters = async () => {
    if (!dateRange[0].startDate || !dateRange[0].endDate) {
      alert("Please select a valid date range.");
      return;
    }
    try {
      const response = await loadEntries(dateRange);
      setReportData(response);
    } catch (error) {
      console.error("Failed to load report:", error);
    }
  };

  const statusKeys = reportData?.[0]?.statusSummary
    ? Object.keys(reportData[0].statusSummary)
    : [];
  const priorityKeys = reportData?.[0]?.prioritySummary
    ? Object.keys(reportData[0].prioritySummary)
    : [];
  const categoryKeys = reportData?.[0]?.categorySummary
    ? Object.keys(reportData[0].categorySummary)
    : [];

  // Sorting handler
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredData = useMemo(() => {
    if (!reportData) return [];
    let data = [...reportData];

    // Filter by search query (agent name)
    if (searchQuery) {
      data = data.filter((row) =>
        row.agentName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort if config is set
    if (sortConfig.key) {
      data.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (typeof valA === "string") valA = valA.toLowerCase();
        if (typeof valB === "string") valB = valB.toLowerCase();

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [reportData, searchQuery, sortConfig]);

  // Sorting icon
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
    }
    if (sortConfig.direction === "asc") {
      return <FontAwesomeIcon icon={faSortUp} className="ml-1 text-blue-400" />;
    }
    return <FontAwesomeIcon icon={faSortDown} className="ml-1 text-blue-400" />;
  };

  const exportToCSV = () => {
    if (!reportData || reportData.length === 0) return;

    const titleRow = ["Agent Entry Summary Report"];
    const dateRow = [
      `Date Range: ${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`
    ];

    const headerRow1 = [
      "Agent",
      "Total Tickets",
      "Tickets Per Day",
      "Avg Resolution Time",
      "Escalated Tickets",
      "Overdue Tickets",
      "No Due Date Tickets",
      ...Array(statusKeys.length).fill("Status"),
      ...Array(priorityKeys.length).fill("Priority"),
      ...Array(categoryKeys.length).fill("Category")
    ];

    const headerRow2 = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      ...statusKeys,
      ...priorityKeys,
      ...categoryKeys
    ];

    const dataRows = reportData.map((row) => [
      row.agentName,
      row.totalTickets,
      roundNumber(row.ticketsPerDay),
      secToHHMMSS(row.avgTime),
      row.escalatedTicketCount,
      row.overdueTickets,
      row.noDueDateTickets,
      ...statusKeys.map((key) => row.statusSummary?.[key] || 0),
      ...priorityKeys.map((key) => row.prioritySummary?.[key] || 0),
      ...categoryKeys.map((key) => row.categorySummary?.[key] || 0)
    ]);

    const csvRows = [titleRow, dateRow, headerRow1, headerRow2, ...dataRows];
    const csvContent = csvRows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "ticket_report.csv");
    link.click();
  };

  const exportToExcel = () => {
    if (!reportData || reportData.length === 0) return;

    const titleRow = ["Agent Entry Summary Report"];
    const dateRow = [
      `Date Range: ${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`
    ];

    const headerRow1 = [
      "Agent",
      "Total Tickets",
      "Tickets Per Day",
      "Avg Resolution Time",
      "Escalated Tickets",
      "Overdue Tickets",
      "No Due Date Tickets",
      ...Array(statusKeys.length).fill("Status"),
      ...Array(priorityKeys.length).fill("Priority"),
      ...Array(categoryKeys.length).fill("Category")
    ];

    const headerRow2 = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      ...statusKeys,
      ...priorityKeys,
      ...categoryKeys
    ];

    const dataRows = reportData.map((row) => [
      row.agentName,
      row.totalTickets,
      roundNumber(row.ticketsPerDay),
      secToHHMMSS(row.avgTime),
      row.escalatedTicketCount,
      row.overdueTickets,
      row.noDueDateTickets,
      ...statusKeys.map((key) => row.statusSummary?.[key] || 0),
      ...priorityKeys.map((key) => row.prioritySummary?.[key] || 0),
      ...categoryKeys.map((key) => row.categorySummary?.[key] || 0)
    ]);

    const wsData = [titleRow, dateRow, headerRow1, headerRow2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: wsData[3].length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: wsData[3].length - 1 } },
      { s: { r: 2, c: 7 }, e: { r: 2, c: 6 + statusKeys.length } },
      { s: { r: 2, c: 7 + statusKeys.length }, e: { r: 2, c: 6 + statusKeys.length + priorityKeys.length } },
      { s: { r: 2, c: 7 + statusKeys.length + priorityKeys.length }, e: { r: 2, c: 6 + statusKeys.length + priorityKeys.length + categoryKeys.length } },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ticket Report");
    XLSX.writeFile(wb, "ticket_report.xlsx");
  };

  return (
    <div className="bg-blue-10 min-h-screen p-1">
      <h2 className="text-2xl text-center font-bold text-blue-600 mb-4">Ticket Entry Summary Report</h2>
      {/* Date Filter */}
      <div className="mb-6 w-full">
        <div className="flex justify-between items-center mb-4">
          <DateRange dateRange={dateRange} setDateRange={setDateRange} label="Select Date Range" />
          <button
            onClick={handleApplyFilters}
            className="bg-blue-500 text-white px-5 py-2 rounded-lg shadow hover:bg-blue-700 transition"
          >
            Apply Filter
          </button>
        </div>
      </div>
      {/* Report Table */}
      {reportData && (
        <div className="bg-white overflow-auto max-h-[600px] rounded-2xl shadow-lg p-4 border border-gray-200">
          {/* Search + Export */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm font-semibold text-gray-700">
              Report Period:{" "}
              <span className="text-blue-600">
                {`${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
              </span>
            </div>
            <div className="flex gap-3 items-center">
              <input
                type="text"
                placeholder="Search Agent..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border px-3 py-2 rounded-lg shadow text-sm"
              />
              <button
                onClick={exportToCSV}
                className="bg-green-500 text-white px-3 py-2 rounded-lg shadow hover:bg-green-600 transition flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faFileCsv} /> CSV
              </button>
              <button
                onClick={exportToExcel}
                className="bg-yellow-500 text-white px-3 py-2 rounded-lg shadow hover:bg-yellow-600 transition flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
            </div>
          </div>

          <table className="min-w-full border-collapse">
            {/* Header */}
            <thead className="sticky top-0 z-10">
              {/* First Header Row */}
              <tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                <th rowSpan={2} onClick={() => requestSort("agentName")} className="cursor-pointer px-6 py-3 border-r">
                  Agent <SortIcon columnKey="agentName" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("totalTickets")} className="cursor-pointer px-6 py-3 border-r">
                  Total Tickets <SortIcon columnKey="totalTickets" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("ticketsPerDay")} className="cursor-pointer px-6 py-3 border-r">
                  Tickets/Day <SortIcon columnKey="ticketsPerDay" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("avgTime")} className="cursor-pointer px-6 py-3 border-r">
                  Avg. Resolution <SortIcon columnKey="avgTime" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("escalatedTicketCount")} className="cursor-pointer px-6 py-3 border-r">
                  Escalated <SortIcon columnKey="escalatedTicketCount" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("overdueTickets")} className="cursor-pointer px-6 py-3 border-r">
                  Overdue <SortIcon columnKey="overdueTickets" />
                </th>
                <th rowSpan={2} onClick={() => requestSort("noDueDateTickets")} className="cursor-pointer px-6 py-3 border-r">
                  No Due Date <SortIcon columnKey="noDueDateTickets" />
                </th>
                <th colSpan={statusKeys.length} className="px-6 py-3 border-r">Status</th>
                <th colSpan={priorityKeys.length} className="px-6 py-3 border-r">Priority</th>
                <th colSpan={categoryKeys.length} className="px-6 py-3">Category</th>
              </tr>

              {/* Second Header Row */}
              <tr className="bg-blue-100 text-blue-800">
                {statusKeys.map((key) => (
                  <th key={key} className="px-4 py-2 text-xs font-semibold border-r">{key}</th>
                ))}
                {priorityKeys.map((key) => (
                  <th key={key} className="px-4 py-2 text-xs font-semibold border-r">{key}</th>
                ))}
                {categoryKeys.map((key) => (
                  <th key={key} className="px-4 py-2 text-xs font-semibold border-r">{key}</th>
                ))}
              </tr>
            </thead>

            {/* Body */}
            <tbody className="divide-y divide-gray-200 text-sm">
              {sortedAndFilteredData.map((row, idx) => (
                <tr key={idx} className="hover:bg-blue-50 odd:bg-gray-50 even:bg-white">
                  <td className="px-6 py-3 font-medium text-gray-900 border-r">{row.agentName}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{row.totalTickets}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{roundNumber(row.ticketsPerDay)}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{secToHHMMSS(row.avgTime)}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{row.escalatedTicketCount}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{row.overdueTickets}</td>
                  <td className="px-6 py-3 text-gray-700 border-r">{row.noDueDateTickets}</td>
                  {statusKeys.map((key, i) => (
                    <td key={i} className="px-4 py-2 text-gray-600 border-r">{row.statusSummary?.[key] || 0}</td>
                  ))}
                  {priorityKeys.map((key, i) => (
                    <td key={i} className="px-4 py-2 text-gray-600 border-r">{row.prioritySummary?.[key] || 0}</td>
                  ))}
                  {categoryKeys.map((key, i) => (
                    <td key={i} className="px-4 py-2 text-gray-600 border-r">{row.categorySummary?.[key] || 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportPage;

